// The title's own presentation VALUES (seconds of the round's runner, design units): the pots, the boosters, the
// collect, the multipliers, the mysteries, the bonus's end, the Rive pots, the flying lights and their impacts, the
// coins a pot spills, the pots' eruptions and fire streaks, the activated pots' smoke, the meter's flames and the lines'
// frame. The only file to edit to retune them.

import type { CoinFountainTuning } from "@slot/presentation";

export const POTS_TUNING = {
  /**
   * A gem's flight from its coin to its pot as the gem's sprite (until the lights are in: a light flies at its own
   * speed, `FLYING_LIGHT_TUNING`), and the gap before the next gem leaves.
   */
  gemFlightSeconds: 0.62,
  gemLift: 260,
  gemSize: 92,
  gemStaggerSeconds: 0.16,
  /** The pot's bump as a gem lands in it. */
  bumpSeconds: 0.28,
  /** The pot's impact plays this long before its gem's light reaches it (its flash leads the light in). */
  impactLeadSeconds: 0.08,
  /** A pot erupting into its booster, and the gap between two pots erupting. */
  eruptSeconds: 0.75,
  eruptStaggerSeconds: 0.25,
  /**
   * As the bonus starts, a booster's fire streak has landed (LIFE's on the meter) and another booster follows: this long
   * more before the next pot erupts, so the landing reads on its own (the user, 2026-10-13: longer before GROW's).
   */
  afterLandingSeconds: 1,
  /** The empty pots after the trigger (the bet changed): how long they take to empty. */
  emptySeconds: 0.2,
} as const;

export const BOARD_TUNING = {
  /** The covered line's (or the full pot's) cells glowing before the trigger panel. */
  triggerHighlightSeconds: 1.1,
  /** GROW growing the grid (and the composition zooming out with it), and back after the bonus. */
  growSeconds: 0.9,
  shrinkSeconds: 0.6,
  /**
   * LIFE's refill: the pot erupts, its fire streak falls onto the meter (the sprite flies this long from the pot until
   * the streaks are in), the meter fills.
   */
  refillFlightSeconds: 0.55,
  /** A value flying into a collect coin, the gap between two, and the collect's count-up per value. */
  collectFlightSeconds: 0.42,
  collectStaggerSeconds: 0.12,
  /**
   * MULTI's badges: its fire streaks fall onto their cells (the sprite flies this long from the pot until the streaks
   * are in), the gap between two, the badge's pop as one lands.
   */
  badgeFlightSeconds: 0.55,
  badgeStaggerSeconds: 0.14,
  badgePopSeconds: 0.32,
  badgeSize: 96,
  /** A mystery's flip to its symbol, and the gap between two. */
  revealSeconds: 0.32,
  revealStaggerSeconds: 0.12,
  /** The full board's jackpot: its plaque celebrates. */
  fullBoardSeconds: 1.4,
  /**
   * The end of the bonus, cell by cell (3 Coin Volcanoes', the user 2026-10-15): the total plaque pops in under the grid; a
   * cell's multiplier moves onto its centre and swells out (`mergeMoveSeconds`, `mergePopSeconds`), the coin's number
   * counting up to what the cell pays (`multiplyCountSeconds`); then a comet carries the amount down to the plaque at
   * `cometSpeed` (design units a second), bowed `cometLift`, as big as a gem's (the user, 2026-10-15), and the plaque
   * steps up (`cellCountSeconds`); `cellGapSeconds` before the next cell. Before the comets are in, the amount flies
   * on its own (`cellFlightSeconds`).
   */
  totalOpenSeconds: 0.3,
  mergeMoveSeconds: 0.22,
  mergePopSeconds: 0.28,
  multiplyCountSeconds: 0.35,
  cometSpeed: 2000,
  cometLift: 180,
  cellFlightSeconds: 0.4,
  cellCountSeconds: 0.18,
  cellGapSeconds: 0.12,
  totalHoldSeconds: 0.6,
  /** The meter's slots popping as they light back up. */
  meterPopSeconds: 0.3,
  /** A respin taking a slot of the meter: its fill draining like a slider, right to left (the respin rolls meanwhile). */
  meterDrainSeconds: 0.35,
} as const;

export const POTION_POTS_TUNING = {
  /** How long they are drawn after a change though every pot is empty (an empty urn is still; its images decode). */
  settleSeconds: 1,
} as const;

export const IMPACT_TUNING = {
  /** An impact's burst (the artist's: 60 frames at 60 a second), drawn for this long after its hit, then hidden. */
  seconds: 1,
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
} as const;

/**
 * The coins a pot spills as a gem's light reaches it, its impact playing (the user, 2026-10-12): the engine's coin
 * fountain thrown once from its opening (`PotCoinBursts`). Seconds of the UI's runner; lengths in design units, the
 * opening's (the anchor's units, at scale 1).
 */
export const COIN_BURST_TUNING = {
  /** Coins per spill, and how high they go (times the room's `apexHeight` share). */
  coins: 12,
  heightScale: 1,
  /** A coin's whole turn about its upright axis, at a spin rate of 1. */
  turnSeconds: 0.45,
  fountain: {
    // Two spills from one pot at once (two of its gems in a row) still have their coins.
    maxCoins: 24,
    gravity: 2600,
    // Out of the potion's surface, about the opening's width.
    spawnHalfSize: { width: 55, height: 6 },
    // Up to 300 over the opening (to the plaques' lower edges, behind them) and about a pot's width to a side.
    room: { above: 300, halfWidth: 230 },
    apexHeight: { min: 0.45, max: 1 },
    sideReach: { min: 0.1, max: 1 },
    // Out by 200 below the opening: the grid's frame (about 200 under it) hides the rest of their fall.
    fadeBelow: { start: 40, length: 160 },
    cullMargin: 120,
    coin: {
      // 35–65 wide (`PotCoinBursts`' coin is 100 at 1): the heap's coins' size and a little more.
      scale: { min: 0.35, max: 0.65 },
      spinRate: { min: 0.7, max: 1.5 },
      tiltDegrees: 20,
      turnDegreesPerSecond: 90,
    },
  } satisfies CoinFountainTuning,
} as const;

export const ART_QUALITY_TUNING = {
  /**
   * The artist's animations' quality per device (the Rive files as Spine skeletons): each drawn frame is a render into a
   * multisampled texture of the art's own, a cost a phone's GPU feels (the user, 2026-10-11: low fps on a phone, under
   * Rive). On a phone those textures are at most this many device pixels per CSS pixel, and its looping art (the pots'
   * potion, the plumes) is drawn this often (engine seconds between two frames, at least); its moments (a comet, an
   * impact, an eruption, a fire streak) keep their own rate.
   */
  phone: { maxDeviceResolution: 1.5, loopFrameSeconds: 1 / 20 },
  desktop: { maxDeviceResolution: 2, loopFrameSeconds: 1 / 30 },
} as const;

export const ART_LOAD_TUNING = {
  /**
   * The longest a round holds for the art the game loads after it starts (decision 71: a spin or a buy waits, its reels
   * rolling and the shell's loading overlay showing): past it the round plays with what is in, the stand-ins for the
   * rest (a stalled network).
   */
  maxWaitSeconds: 20,
} as const;

export const EDGE_FIRE_TUNING = {
  /** Its textures' share of the screen's pixels. */
  resolution: 1,
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
  /** Drawn on this long after it goes off: its dying out (the artist's 0.4 s), then hidden. */
  offSeconds: 0.45,
  /** It burns on this long after the grid has grown (the UI's time), then dies out (the user, 2026-10-12: longer). */
  afterGrowSeconds: 2,
} as const;

export const CELL_FIRE_TUNING = {
  /** A hit's burst (the artist's: 48 frames at 60 a second), drawn this long after it plays, then hidden. */
  hitSeconds: 0.8,
  /** Hits that can burst at once (the file loaded once): MULTI's next lands as the last one's burst plays out. */
  hits: 2,
  /** Cells that can burn at once, a fire each (loaded once): a cell past them shows its multiplier without one. */
  fires: 12,
  /** A fire still drawn this long after it goes out (the artist's 0.4 s dying down), then hidden. */
  offSeconds: 0.45,
  /** A hit's drawn frames (engine seconds between two, at least); a burning cell loops, at the device's rate. */
  frameSeconds: 1 / 30,
  /** Their textures' share of the screen's pixels. */
  resolution: 1,
} as const;

/**
 * A mystery jackpot's reveal (the designer's mystery reel, 2026-10-12; `MysteryReel`): seconds of the round's runner.
 * Its rise to the board's middle, `grow` times its board size (its frame then about half the board's width), its text
 * gone in the first `textFadeShare` of it; its spin past `spinBands` jackpot bands to the jackpot, `overshoot` the back
 * ease's (about 1 % of the way past it); the band lit (its frame in over `winFrameInShare` of it, the shine across in
 * `shineShare`, each sparkle `sparkleShare` long); its return to the cell.
 */
export const MYSTERY_REEL_TUNING = {
  grow: 2.3,
  riseSeconds: 0.55,
  textFadeShare: 0.5,
  spinSeconds: 2.4,
  spinBands: 9,
  overshoot: 0.6,
  winSeconds: 1.1,
  winFrameInShare: 0.2,
  shineShare: 0.55,
  sparkleShare: 0.3,
  returnSeconds: 0.45,
} as const;

export const COIN_SHINE_TUNING = {
  /** The idle coin's variants drawn (one artboard each, the file loaded once): its own 0 to 3, every plain coin one of them. */
  variants: 4,
  /** Their textures' share of the screen's pixels. */
  resolution: 1,
} as const;

export const COLLECTOR_TUNING = {
  /**
   * Collect coins that show the artist's collector at once (the file loaded once, an artboard each): one past them shows
   * the delivery's red coin. The bonus's board holds a few at most.
   */
  collectors: 6,
  /** Their textures' share of the screen's pixels. */
  resolution: 1,
} as const;

export const BOARD_IMPACT_TUNING = {
  /** Its burst (the artist's: 66 frames at 60 a second), drawn this long after its hit, then hidden. */
  seconds: 1.1,
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
  /** Its texture's share of the screen's pixels. */
  resolution: 1,
  /**
   * The frame catches fire and the grid starts to grow this long after the hit: the artist's frame 8, its shockwave
   * reaching the frame's top and bottom edges.
   */
  growAfterSeconds: 8 / 60,
} as const;

export const POT_SHINE_TUNING = {
  /** Its texture's share of the screen's pixels: soft rays need few (the artist's own sample draws it at half). */
  resolution: 0.5,
} as const;

export const POT_ERUPTION_TUNING = {
  /** An eruption (the artist's: 54 frames at 60 a second), drawn this long after its trigger, then hidden. */
  seconds: 1,
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
  /** A pot's fire streaks set out this long after it erupts (the artist's 15–25 frames: its jets leave the screen). */
  streaksAfterSeconds: 0.35,
} as const;

export const FIRE_STREAK_TUNING = {
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
  /**
   * Its size: this many times the mock-up's (the user, 2026-10-12: half as big again, then a little bigger), its fireball,
   * trail and ring alike.
   */
  scale: 1.75,
  /** Its fall's speed, design units a second, straight down from over the screen's top onto its target. */
  speed: 3200,
  /** How far over the screen's top its front sets out (the fireball out of sight as it sets out, whatever its size). */
  clearance: 40,
  /** Its landing (the artist's 45 frames at 60 a second, its ring gone by then): drawn this long, then put back. */
  landSeconds: 0.8,
  /**
   * A MULTI streak's number, caught on its cell's centre as the streak's head gets there, pops there first (out to
   * `labelPop.peak` times its size and back, over `labelPop.seconds`), then scales down to its own size and moves to the
   * cell's corner over `labelSeconds` (the user, 2026-10-12: a pop, then a little slow).
   */
  labelPop: { seconds: 0.18, peak: 1.25 },
  labelSeconds: 0.5,
  /**
   * MULTI's streaks that carry no number and land nowhere, falling on past the screen's bottom down a column no
   * multiplier lands in, among those that land (the user, 2026-10-12): how many each time it throws (the free columns
   * nearest the middle first); how long after the throw's first landing streak the first sets out, so it falls well
   * behind it (at the streaks' speed, about 700 units over it; the user: more vertical offset); and the gap after.
   */
  passers: { count: 1, afterSeconds: 0.22, gapSeconds: 0.16 },
  /** Each pot's streaks (the file loaded once): MULTI's next falls as the last one's ring plays out, its passers besides. */
  perPot: { life: 1, multi: 6, grow: 1 },
} as const;

export const POT_SMOKE_TUNING = {
  /** How long a plume is still drawn after it goes off (the artist's: it dissipates in about 0.3 s), then hidden. */
  offSeconds: 0.4,
  /** Its canvas at this share of the screen's pixels: soft smoke (the artist's own half). */
  resolution: 0.5,
} as const;

export const FLYING_LIGHT_TUNING = {
  /** The comet's head across, in design units (the gem it carries, as big as the gem was): the comet is ~4.6 times as long. */
  headSize: 92,
  /** Its drawn frames (engine seconds between two, at least). */
  frameSeconds: 1 / 30,
  /**
   * The tightest turn a light's path takes, in design units: its loop over a pot, and the most a downward flight bows
   * (the comet is a rope as wide as its texture, about 160 units: a tighter turn folds its inner side over).
   */
  minTurnRadius: 120,
  /** Into a pot: its straight drop at the end, and its rise's bow, a share of its chord. */
  dropLength: 40,
  riseBow: 0.12,
  /**
   * Its speed in design units a second where its path is straight or curves gently (at least `fullSpeedRadius` round):
   * a flight's time is its path's. Round a tighter curve it flies slower, by the cube root of the curve's radius over
   * that one (the two-thirds power law: the eye reads it as even), the curve measured over `turnSmoothing` either side
   * of the head, so it eases into a turn and out of it (about a sixteenth of a second's flight: raise it with the
   * speed).
   */
  speed: 3200,
  fullSpeedRadius: 800,
  turnSmoothing: 200,
  /** It fades in out of its start over this share of the flight, and out over its last seconds as its head dives in. */
  appearShare: 0.15,
  fadeSeconds: 0.05,
} as const;

export const METER_FLAMES_TUNING = {
  /** The fire's flipbook: frames a second (the artist's, a loop of 2.5 s). */
  fps: 24,
  /** The flames coming as LIFE's booster lights the bonus, going as its refill is spent. */
  fadeSeconds: 0.4,
  /**
   * The sparks, as the artist's preview sends them up: one every so often (a gap between these seconds), each rising
   * this many artboard pixels a second for this long, swaying across, at this size of its image.
   */
  sparkEverySeconds: [0.25, 0.6],
  sparkSeconds: [1.4, 2.2],
  sparkRise: [45, 70],
  sparkSway: 14,
  sparkScale: [0.45, 0.9],
} as const;

export const LINES_TUNING = {
  /** The row frame sliding and stretching to the new active lines' rows as the player changes the lines (decision 56). */
  frameResize: { seconds: 0.3, easing: "cubicOut" },
} as const;
